/**
 * Hook for DB-backed user settings (encrypted API keys).
 * Uses a module-level cache so settings are available instantly
 * after the first successful DB fetch — no flash of empty fields
 * when components mount/unmount across tab switches.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

export interface UserSettings {
  gemini_api_key?: string;
  gemini_model?: string;
  anthropic_api_key?: string;
  claude_model?: string;
  anthropic_proxy_url?: string;
  openai_api_key?: string;
  openai_model?: string;
  xai_api_key?: string;
  xai_model?: string;
  llm_provider?: string;
  suno_cookie?: string;
  suno_proxy_url?: string;
  suno_model?: string;
}

// localStorage keys that map to settings fields
const LS_KEY_MAP: Record<keyof UserSettings, string> = {
  gemini_api_key: 'gemini_api_key',
  gemini_model: 'gemini_model',
  anthropic_api_key: 'anthropic_api_key',
  claude_model: 'claude_model',
  anthropic_proxy_url: 'anthropic_proxy_url',
  openai_api_key: 'openai_api_key',
  openai_model: 'openai_model',
  xai_api_key: 'xai_api_key',
  xai_model: 'xai_model',
  llm_provider: 'llm_provider',
  suno_cookie: 'suno_cookie',
  suno_proxy_url: 'suno_proxy_url',
  suno_model: 'suno_model',
};

/** Sync settings to localStorage so existing code that reads localStorage still works */
function syncToLocalStorage(settings: UserSettings): void {
  for (const [field, lsKey] of Object.entries(LS_KEY_MAP)) {
    const value = settings[field as keyof UserSettings];
    if (value) {
      localStorage.setItem(lsKey, value);
    }
  }
}

/** Read current localStorage values as settings (for initial/fallback) */
function readFromLocalStorage(): UserSettings {
  const settings: UserSettings = {};
  for (const [field, lsKey] of Object.entries(LS_KEY_MAP)) {
    const value = localStorage.getItem(lsKey);
    if (value) {
      (settings as Record<string, string>)[field] = value;
    }
  }
  return settings;
}

// ── Module-level singleton cache ─────────────────────────────────
// Survives component mount/unmount cycles so the DB fetch only happens once.
let _cachedSettings: UserSettings | null = null;
let _fetchPromise: Promise<UserSettings> | null = null;
let _listeners: Set<(s: UserSettings) => void> = new Set();

function notifyListeners(settings: UserSettings) {
  _listeners.forEach(fn => fn(settings));
}

async function fetchSettingsFromDB(): Promise<UserSettings> {
  const res = await fetchWithCsrf(API_ENDPOINTS.settings);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.raw || {};
}

async function loadSettings(): Promise<UserSettings> {
  // Already fetched — return cache
  if (_cachedSettings) return _cachedSettings;

  // Fetch in progress — wait for it
  if (_fetchPromise) return _fetchPromise;

  // Start fetch with retry
  _fetchPromise = (async () => {
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise(r => setTimeout(r, attempt * 2000));
        }
        const raw = await fetchSettingsFromDB();

        if (Object.keys(raw).length > 0) {
          _cachedSettings = raw;
          syncToLocalStorage(raw);
          notifyListeners(raw);
          return raw;
        } else {
          // DB empty — seed from localStorage
          const lsSettings = readFromLocalStorage();
          if (Object.keys(lsSettings).length > 0) {
            await fetchWithCsrf(API_ENDPOINTS.settings, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(lsSettings),
            });
          }
          _cachedSettings = lsSettings;
          notifyListeners(lsSettings);
          return lsSettings;
        }
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        console.warn(`🔒 Settings load attempt ${attempt + 1}/3 failed:`, lastError.message);
      }
    }
    // All retries failed — fall back to localStorage
    const fallback = readFromLocalStorage();
    _cachedSettings = null; // Don't cache failure — allow retry on next mount
    _fetchPromise = null;
    throw lastError;
  })();

  return _fetchPromise;
}

export function useUserSettings() {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(
    () => _cachedSettings || readFromLocalStorage()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  // Subscribe to updates from other hook instances
  useEffect(() => {
    const listener = (s: UserSettings) => {
      if (mounted.current) setSettings(s);
    };
    _listeners.add(listener);
    return () => {
      _listeners.delete(listener);
      mounted.current = false;
    };
  }, []);

  // Load settings from DB on auth
  useEffect(() => {
    if (!isAuthenticated) return;

    // If already cached, just use it (instant — no loading flash)
    if (_cachedSettings) {
      setSettings(_cachedSettings);
      return;
    }

    let cancelled = false;
    setLoading(true);

    loadSettings()
      .then(raw => {
        if (!cancelled) {
          setSettings(raw);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.warn('🔒 Failed to load user settings from DB, using localStorage:', err);
          setError(err instanceof Error ? err.message : 'Failed to load settings');
          setSettings(readFromLocalStorage());
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Save settings to DB, update cache, sync to localStorage, notify all instances
  const save = useCallback(async (updates: Partial<UserSettings>): Promise<boolean> => {
    try {
      const merged = { ...(_cachedSettings || settings), ...updates };

      // Update cache + localStorage + all hook instances immediately
      _cachedSettings = merged;
      syncToLocalStorage(merged);
      setSettings(merged);
      notifyListeners(merged);

      const res = await fetchWithCsrf(API_ENDPOINTS.settings, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return true;
    } catch (err) {
      console.error('🔒 Failed to save settings to DB:', err);
      return false;
    }
  }, [settings]);

  return { settings, loading, error, save };
}
